import React, { useState, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import styles from './PasswordConfirmationModal.module.css';
import { verifyPasswordReq } from '../../../axios/requests/loginReq';

const PasswordConfirmationModal = ({ show, onHide, onConfirm, titleKey, messageKey, confirmButtonText, confirmButtonVariant = 'success' }) => {
    const { t, i18n } = useTranslation('crm');
    const loggedUser = useSelector(state => state.login);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const isRTL = i18n.language === 'he';

    const handlePasswordChange = useCallback((e) => {
        setPassword(e.target.value);
        setError(''); // Clear error when user types
    }, []);

    const handleConfirm = useCallback(async (e) => {
        e.preventDefault();

        if (!password) {
            setError(t('Password is required'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            // Verify password using dashboard login endpoint
            const userData = await verifyPasswordReq(loggedUser.email, password);

            // Double-check permission from the server response
            if (userData?.permissionType > 3) {
                setError(t('Insufficient permissions for this operation.'));
                setLoading(false);
                return;
            }

            // Password is correct and permission is valid, proceed with the action
            onConfirm();

            // Reset and close
            setPassword('');
            setError('');
            onHide();
        } catch (err) {
            setError(t('Incorrect password. Please try again.'));
        } finally {
            setLoading(false);
        }
    }, [password, loggedUser.email, onConfirm, onHide, t]);

    const handleClose = useCallback(() => {
        setPassword('');
        setError('');
        onHide();
    }, [onHide]);

    return (
        <Modal show={show} onHide={handleClose} centered>
            <div dir={isRTL ? 'rtl' : 'ltr'}>
                <Modal.Header>
                    <Modal.Title>{t(titleKey)}</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleConfirm}>
                    <Modal.Body className={styles['modal-body']} dir={isRTL ? 'rtl' : 'ltr'}>
                        <p className={styles['message']}>{t(messageKey)}</p>
                        <div className={styles['form-group']}>
                            <label htmlFor="password">{t('Password')}</label>
                            <input
                                id="password"
                                type="password"
                                className={styles['password-input']}
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder={t('Enter your password')}
                                autoFocus
                                disabled={loading}
                            />
                            {error && <p className={styles['error-message']}>{error}</p>}
                        </div>
                    </Modal.Body>
                    <Modal.Footer dir={isRTL ? 'rtl' : 'ltr'}>
                        <Button
                            variant={confirmButtonVariant}
                            type="submit"
                            disabled={loading || !password}
                        >
                            {loading ? t('Verifying...') : (confirmButtonText || t('Confirm'))}
                        </Button>
                        <Button variant="secondary" onClick={handleClose} disabled={loading}>
                            {t('Cancel')}
                        </Button>
                    </Modal.Footer>
                </form>
            </div>
        </Modal>
    );
};

export default PasswordConfirmationModal;
